/**
 * Compiles the quadratic-core wasm exactly once on the main thread. The
 * resulting `WebAssembly.Module` is shared with the web workers via
 * `postMessage` so every thread instantiates the same compiled code instead of
 * each fetching and compiling its own (~26 MB raw) copy. Engines share the
 * compiled machine code across instantiations of a single `Module`, so this
 * removes the duplicated wasm code memory on the light threads.
 *
 * `WebAssembly.Module` is structured-clone serializable (not transferable), so
 * it is passed in the message body and must not be placed in a transfer list.
 */

import wasmUrl from '@/app/quadratic-core/quadratic_core_bg.wasm?url';
import { attemptChunkErrorReload, isReloadPending } from '@/shared/utils/chunkErrorHandler';
import { isWASMSupported } from '@/shared/utils/isWASMSupported';

/**
 * Thrown when the wasm asset can't be loaded as served. Two client/transport
 * conditions produce it, neither an app bug, and both recoverable by
 * re-downloading:
 *
 * - Stale bundle after a deploy: the cached index.html/JS references an old
 *   hashed asset (e.g. `quadratic_core_bg-<hash>.wasm`) that the CDN now
 *   403/404s, serving the SPA HTML fallback in its place. Feeding that HTML into
 *   `WebAssembly.compile` throws an opaque "expected magic word 00 61 73 6d,
 *   found 3c 21 44 4f" (`<!DO...`) CompileError.
 * - Truncated download: the response is real wasm (magic header checks out) but
 *   the body ended early, so compilation fails partway through a section. See
 *   `isTruncatedWasmError`.
 */
export class WasmAssetUnavailableError extends Error {
  /**
   * True when a page reload that will re-download the asset is in flight —
   * either kicked off for this failure, or already requested earlier by this
   * same document.
   */
  readonly reloadTriggered: boolean;

  constructor(message: string, reloadTriggered: boolean) {
    super(message);
    this.name = 'WasmAssetUnavailableError';
    this.reloadTriggered = reloadTriggered;
  }
}

export const isWasmAssetUnavailableError = (e: unknown): e is WasmAssetUnavailableError =>
  e instanceof WasmAssetUnavailableError;

/**
 * Thrown when the engine can't compile our core no matter how it is served,
 * because it predates the wasm features `wasm-bindgen` emits. `isWASMSupported`
 * probes for exactly that (basic wasm plus the `externref` reference types
 * shipped in Chrome 96 / Firefox 102 / Safari 16).
 *
 * This is a browser-support condition, not a failure: `routes/file.tsx` already
 * renders the "Browser not supported" page for it and reports it to Sentry at
 * `info`. Distinguishing it here keeps the ~27 MB core from being downloaded to
 * a browser that provably cannot run it, and keeps the consumers' `.catch` from
 * logging a second, `error`-level report of the same fact.
 */
export class WasmUnsupportedBrowserError extends Error {
  constructor() {
    super('browser does not support the WebAssembly features required by quadratic-core (reference types)');
    this.name = 'WasmUnsupportedBrowserError';
  }
}

export const isWasmUnsupportedBrowserError = (e: unknown): e is WasmUnsupportedBrowserError =>
  e instanceof WasmUnsupportedBrowserError;

let modulePromise: Promise<WebAssembly.Module> | null = null;

export const compileWasmModule = (): Promise<WebAssembly.Module> => {
  if (!modulePromise) {
    modulePromise = compile();
  }
  return modulePromise;
};

// The first 4 bytes of every WebAssembly binary: `\0asm`.
const WASM_MAGIC = [0x00, 0x61, 0x73, 0x6d];

const looksLikeWasm = (bytes: ArrayBuffer): boolean => {
  if (bytes.byteLength < WASM_MAGIC.length) return false;
  const header = new Uint8Array(bytes, 0, WASM_MAGIC.length);
  return WASM_MAGIC.every((b, i) => header[i] === b);
};

// A stale hashed wasm asset after a deploy is recoverable by reloading once to
// pick up the fresh index.html/asset hashes. Reuse the shared chunk-error
// reload so the wasm and JS-chunk cases share a single reload guard (no loops).
const handleStaleAsset = (message: string): WasmAssetUnavailableError => {
  // `attemptChunkErrorReload` returns false once the one-shot guard is spent,
  // but that alone doesn't mean recovery failed. When this same document already
  // requested the reload, the navigation is still pending — and it is what cut
  // this ~27 MB download short in the first place, since a pending navigation
  // cancels in-flight requests. The reload will re-fetch the asset, so treat it
  // as recovering rather than as a persistent post-reload failure. Only a guard
  // spent by an *earlier* document (`isReloadPending()` false) is genuine.
  const reloadTriggered = attemptChunkErrorReload() || isReloadPending();
  return new WasmAssetUnavailableError(message, reloadTriggered);
};

// How V8, SpiderMonkey and JavaScriptCore each report a module whose bytes ran
// out mid-decode. On a body that already passed the magic-header check these
// mean the download was cut short — the core is ~27 MB, so an aborted
// connection, a proxy or crawler capping response size, or a flaky mobile link
// can all deliver a prefix of it. A correctly served, fully downloaded wasm
// always compiles, so this is a transport failure rather than a bad build.
//
// Matched narrowly on purpose: a CompileError from a wasm feature the engine
// doesn't support (missing SIMD/bulk-memory) is NOT fixable by re-downloading,
// so it must keep reporting as a real error instead of spending a reload on it.
// The one such gap we can name up front — reference types on a pre-2022 engine —
// is caught by the `isWASMSupported` check above before the fetch.
const WASM_TRUNCATION_PATTERNS = [
  /extends past end of the module/,
  /unexpected end of/,
  /length \d+, remaining bytes \d+/,
  /unable to decode section/,
];

const isTruncatedWasmError = (e: unknown): e is WebAssembly.CompileError =>
  e instanceof WebAssembly.CompileError && WASM_TRUNCATION_PATTERNS.some((pattern) => pattern.test(e.message));

// A non-ok response means the hashed asset is gone (stale deploy). Recover via
// reload instead of trying to compile the SPA HTML fallback body.
const assertAssetServed = (response: Response) => {
  if (!response.ok) {
    throw handleStaleAsset(`wasm asset request failed: ${response.status} ${response.statusText}`);
  }
};

const compile = async (): Promise<WebAssembly.Module> => {
  try {
    // Bail before the fetch on browsers that fail the reference-types probe.
    // They would download ~27 MB and then throw "invalid value type 'externref'"
    // from `WebAssembly.compile` (INTERNAL-6KM, Chrome 79), which reads like a
    // corrupt build but is just an engine too old to run us.
    if (!isWASMSupported) {
      throw new WasmUnsupportedBrowserError();
    }

    const response = await fetch(wasmUrl);
    assertAssetServed(response);

    // `compileStreaming` requires the server to respond with `application/wasm`.
    // Fall back to a buffered compile when it is unavailable or the mime type is
    // wrong (e.g. some dev/proxy setups).
    let buffered = response;
    if (typeof WebAssembly.compileStreaming === 'function') {
      try {
        return await WebAssembly.compileStreaming(response);
      } catch (e) {
        // compileStreaming throws when the response mime type isn't
        // `application/wasm`, but also for network errors, CSP violations, or a
        // corrupt module. Log so those non-mime failures are debuggable, then
        // fall through to the buffered compile.
        console.warn('[compileWasmModule] compileStreaming failed, falling back to buffered compile:', e);
        // The streaming attempt owns `response`'s body, so the buffered compile
        // needs a request of its own. It must also skip the HTTP cache: one way
        // compileStreaming fails is the browser aborting the body mid-stream
        // because it could not write the (~26 MB) asset to its disk cache
        // (`ERR_CACHE_WRITE_FAILURE`), and a cached-but-truncated entry would
        // fail the retry the same way.
        buffered = await fetch(wasmUrl, { cache: 'no-store' });
        assertAssetServed(buffered);
      }
    }
    const bytes = await buffered.arrayBuffer();

    // Guard against a 200 that's actually the HTML SPA fallback (some hosts
    // serve a missing asset as 200 index.html rather than 403/404). Same
    // stale-deploy condition — recover rather than emit an opaque CompileError.
    if (!looksLikeWasm(bytes)) {
      throw handleStaleAsset('wasm asset response is not a WebAssembly binary (likely stale deploy)');
    }

    try {
      return await WebAssembly.compile(bytes);
    } catch (e) {
      // The magic header checked out, so this isn't the HTML fallback — it's
      // real wasm that arrived incomplete. Recover the same way as a stale
      // asset (one guarded reload to re-download) instead of surfacing an
      // opaque CompileError that reads like a corrupt build.
      if (isTruncatedWasmError(e)) {
        throw handleStaleAsset(`wasm asset download was truncated (received ${bytes.byteLength} bytes): ${e.message}`);
      }
      throw e;
    }
  } catch (e) {
    // Clear the cached promise so a later call can retry instead of forever
    // returning this rejected promise (e.g. a transient network failure).
    modulePromise = null;
    throw e;
  }
};
